// src/pages/CompanyProfile.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchEventCompanyById } from '../api/eventCompanies';
import { fetchEventsByCompany } from '../api/events';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactButton from '../components/ContactButton';
import CompanyBookingForm from '../components/CompanyBookingForm';
import './CompanyProfile.css';

const CompanyProfile = () => {
  const { id } = useParams();
  const [company, setCompany] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('about');
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const loadCompanyData = async () => {
      try {
        setLoading(true);
        
        // Charger les données de la société
        const companyData = await fetchEventCompanyById(id);
        setCompany(companyData);
        
        // Charger les événements de la société
        try {
          const eventsData = await fetchEventsByCompany(id, 8);
          setEvents(eventsData);
        } catch (eventsError) {
          console.warn('Impossible de charger les événements:', eventsError);
          setEvents([]);
        }
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadCompanyData();
  }, [id]);

  // Fonction simplifiée pour formater les spécialités
  const formatSpecialtiesForTitle = (specialties) => {
    if (!specialties || !Array.isArray(specialties) || specialties.length === 0) {
      return 'Événementiel';
    }
    if (specialties.length === 1) return specialties[0];
    if (specialties.length === 2) return specialties.join(' & ');
    return `${specialties[0]} & Plus`;
  };

  const formatSpecialtiesForSEO = (specialties) => {
    if (!specialties || !Array.isArray(specialties) || specialties.length === 0) {
      return 'Organisation d\'événements';
    }
    return specialties.join(', ');
  };

  if (loading) {
    return (
      <div className="company-profile-loading">
        <Header />
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="company-profile-error">
        <Helmet>
          <title>Société non trouvée | Gates of Music</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <Header />
        <div className="error-content">
          <h1>Société non trouvée</h1>
          <p>Cette société n'existe pas ou a été supprimée.</p>
          <Link to="/" className="back-home-btn">
            Retour à l'accueil
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Variables SEO optimisées
  const gallery = events.flatMap(event => event.images || []).slice(0, 12);
  const specialtiesForTitle = formatSpecialtiesForTitle(company.specialties);
  const specialtiesForSEO = formatSpecialtiesForSEO(company.specialties);
  const location = company.location || 'Maroc';
  const description = company.description || company.description_full || '';
  const shortDescription = description.substring(0, 155);

  // Créer les données structurées de manière sûre
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "EventPlanner",
    "name": company.name,
    "url": `https://www.gatesofmusic.com/companies/${company.id}`,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": location,
      "addressCountry": "MA"
    }
  };

  // Ajouter les champs optionnels uniquement s'ils existent
  if (description) {
    structuredData.description = description;
  }
  
  if (company.logo || company.coverImage) {
    structuredData.image = company.logo || company.coverImage;
  }
  
  if (company.specialties && company.specialties.length > 0) {
    structuredData.knowsAbout = company.specialties;
  }

  if (company.rating) {
    structuredData.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": company.rating,
      "reviewCount": company.reviewCount || events.length || 1
    };
  }

  return (
    <>
      <Helmet>
        <title>{`${company.name} - ${specialtiesForTitle} à ${location} | Gates of Music`}</title>
        <meta 
          name="description" 
          content={`Réservez ${company.name}, spécialiste en ${specialtiesForSEO} à ${location}. ${shortDescription}`} 
        />
        <link rel="canonical" href={`https://www.gatesofmusic.com/companies/${company.id}`} />
        
        {/* Open Graph optimisé */}
        <meta property="og:title" content={`${company.name} - ${specialtiesForTitle} | Gates of Music`} />
        <meta property="og:description" content={`Réservez ${company.name}, organisateur événementiel à ${location}. ${shortDescription}`} />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={`https://www.gatesofmusic.com/companies/${company.id}`} />
        {(company.coverImage || company.logo) && (
          <meta property="og:image" content={company.coverImage || company.logo} />
        )}
        
        {/* Twitter Cards */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${company.name} - ${specialtiesForTitle}`} />
        <meta name="twitter:description" content={`Organisateur événementiel à ${location}. ${shortDescription}`} />
        {(company.coverImage || company.logo) && (
          <meta name="twitter:image" content={company.coverImage || company.logo} />
        )}

        {/* Données structurées simplifiées et sûres */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <article className="company-profile">
        <Header />
        
        {/* Hero Section */}
        <header className="company-hero">
          {company.coverImage && (
            <div 
              className="company-hero-bg"
              style={{ backgroundImage: `url(${company.coverImage})` }}
            >
              <div className="hero-overlay"></div>
            </div>
          )}
          
          <div className="company-hero-content">
            <div className="company-main-info">
              {company.logo && (
                <img 
                  src={company.logo} 
                  alt={`${company.name} - Organisateur événementiel ${location}`}
                  className="company-logo-large"
                  loading="eager"
                  width="200"
                  height="200"
                />
              )}
              
              <div className="company-title-section">
                <div className="title-row">
                  <h1 className="company-name-large">{company.name}</h1>
                  {company.verified && (
                    <div className="verified-badge-large">
                      <span className="verified-icon">✓</span>
                      Vérifié
                    </div>
                  )}
                </div>
                
                {company.location && (
                  <p className="company-location-large">
                    📍 {company.location}
                  </p>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Navigation Tabs */}
        <nav className="profile-navigation">
          <div className="nav-tabs">
            <button 
              className={`nav-tab ${activeTab === 'about' ? 'active' : ''}`}
              onClick={() => setActiveTab('about')}
              aria-label="À propos"
            >
              À propos
            </button>
            <button 
              className={`nav-tab ${activeTab === 'services' ? 'active' : ''}`}
              onClick={() => setActiveTab('services')}
              aria-label="Services"
            >
              Services
            </button>
            <button 
              className={`nav-tab ${activeTab === 'portfolio' ? 'active' : ''}`}
              onClick={() => setActiveTab('portfolio')}
              aria-label="Portfolio"
            >
              Portfolio
            </button>
            <button 
              className={`nav-tab ${activeTab === 'contact' ? 'active' : ''}`}
              onClick={() => setActiveTab('contact')}
              aria-label="Contact"
            >
              Contact
            </button>
          </div>
        </nav>

        {/* Content Sections */}
        <main className="profile-content">
          {activeTab === 'about' && (
            <section className="about-section">
              <div className="section-header">
                <h2>À propos de {company.name}</h2>
              </div>
              
              <div className="about-content">
                <div className="description">
                  <p>{company.description_full || company.description}</p>
                </div>

                {company.specialties && company.specialties.length > 0 && (
                  <div className="specialties-section">
                    <h3>Nos spécialités</h3>
                    <div className="specialties-grid">
                      {company.specialties.map((specialty, index) => (
                        <div key={index} className="specialty-card">
                          <span className="specialty-name">{specialty}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {activeTab === 'services' && (
            <section className="services-section">
              <div className="section-header">
                <h2>Nos Services</h2>
              </div>
              
              <div className="services-grid">
                <div className="service-card">
                  <div className="service-icon">🎵</div>
                  <h3>Animation Musicale</h3>
                  <p>DJ, orchestres, groupes live pour tous vos événements</p>
                </div>
                
                <div className="service-card">
                  <div className="service-icon">🎭</div>
                  <h3>Spectacles</h3>
                  <p>Artistes, danseurs, performers pour animer vos soirées</p>
                </div>
                
                <div className="service-card">
                  <div className="service-icon">🎨</div>
                  <h3>Décoration</h3>
                  <p>Décoration sur mesure selon vos goûts et votre budget</p>
                </div>
                
                <div className="service-card">
                  <div className="service-icon">🍽️</div>
                  <h3>Traiteur</h3>
                  <p>Service traiteur avec menu personnalisé</p>
                </div>
                
                <div className="service-card">
                  <div className="service-icon">📸</div>
                  <h3>Photo/Vidéo</h3>
                  <p>Capturer tous les moments précieux de votre événement</p>
                </div>
                
                <div className="service-card">
                  <div className="service-icon">📋</div>
                  <h3>Organisation Complète</h3>
                  <p>Coordination de tous les aspects de votre événement</p>
                </div>
              </div>
            </section>
          )}

          {activeTab === 'portfolio' && (
            <section className="portfolio-section">
              <div className="section-header">
                <h2>Nos Réalisations</h2>
                <p>Découvrez nos événements organisés avec succès</p>
              </div>
              
              {events.length > 0 ? (
                <div className="events-portfolio">
                  {/* Galerie d'images si disponible */}
                  {gallery.length > 0 && (
                    <div className="portfolio-gallery">
                      <div className="main-image">
                        <img 
                          src={gallery[selectedImage]} 
                          alt={`Portfolio ${company.name} - ${selectedImage + 1}`}
                          className="portfolio-main-img"
                          loading="lazy"
                        />
                      </div>
                      
                      {gallery.length > 1 && (
                        <div className="thumbnail-gallery">
                          {gallery.slice(0, 8).map((image, index) => (
                            <img
                              key={index}
                              src={image}
                              alt={`Portfolio miniature ${index + 1}`}
                              className={`thumbnail ${index === selectedImage ? 'active' : ''}`}
                              onClick={() => setSelectedImage(index)}
                              loading="lazy"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Liste des événements */}
                  <div className="events-list">
                    <h3>Nos Derniers Événements</h3>
                    <div className="events-grid">
                      {events.map((event) => (
                        <article key={event.id} className="event-card">
                          {event.coverImage && (
                            <img 
                              src={event.coverImage} 
                              alt={event.title}
                              className="event-card-image"
                              loading="lazy"
                            />
                          )}
                          
                          <div className="event-card-content">
                            <div className="event-header">
                              <h4 className="event-title">{event.title}</h4>
                              {event.type && (
                                <span className="event-type">{event.type}</span>
                              )}
                            </div>
                            
                            {event.description && (
                              <p className="event-description">
                                {event.description.substring(0, 120)}...
                              </p>
                            )}
                            
                            <div className="event-details">
                              {event.venue && (
                                <span className="event-venue">
                                  📍 {event.venue}
                                </span>
                              )}
                              {event.guestCount && (
                                <span className="event-guests">
                                  👥 {event.guestCount} invités
                                </span>
                              )}
                              {event.date && (
                                <time className="event-date" dateTime={event.date}>
                                  📅 {new Date(event.date).toLocaleDateString('fr-FR')}
                                </time>
                              )}
                            </div>

                            {event.services && event.services.length > 0 && (
                              <div className="event-services">
                                {event.services.slice(0, 3).map((service, idx) => (
                                  <span key={idx} className="service-tag">
                                    {service}
                                  </span>
                                ))}
                                {event.services.length > 3 && (
                                  <span className="service-tag more">
                                    +{event.services.length - 3}
                                  </span>
                                )}
                              </div>
                            )}

                            {event.testimonial && event.testimonial.comment && (
                              <blockquote className="event-testimonial">
                                <p>"{event.testimonial.comment.substring(0, 80)}..."</p>
                                {event.testimonial.clientName && (
                                  <cite>- {event.testimonial.clientName}</cite>
                                )}
                              </blockquote>
                            )}
                          </div>
                        </article>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="no-portfolio">
                  <p>Portfolio en cours de mise à jour...</p>
                </div>
              )}
            </section>
          )}

          {activeTab === 'contact' && (
            <section className="contact-section">
              <div className="section-header">
                <h2>Contactez-nous</h2>
                <p>Discutons de votre projet ensemble</p>
              </div>
              
              <div className="contact-form-wrapper">
                <CompanyBookingForm 
                  companyId={company.id} 
                  companyName={company.name}
                />
              </div>
            </section>
          )}
        </main>

        <ContactButton />
        <Footer />
      </article>
    </>
  );
};

export default CompanyProfile;